import { useEffect, useMemo, useRef, useState } from "react";
import { UserCheck } from "lucide-react";
import { useNavigate, useLocation } from "react-router";
import logo from "../../assets/images/logo/InstallationSystems.png";
import { useAppDispatch, useAppSelector } from "../../stores/hooks";
import {
  setLogout,
  setSelectedRegion,
} from "@penn/web-common";
import { cognitoSignOut } from "@penn/web-common";
import { endSession } from "@penn/web-common";
import { OIDC_ENABLED, registerDestination } from "@penn/web-common";
import { ADMIN_TAB_NAME, runtimeEnv } from "@penn/web-common";
import { logoutRequest } from "@penn/web-common";
import { stopSessionRefresh } from "@penn/web-common";
import {
  TOKEN_STORAGE_KEY,
  USER_STORAGE_KEY,
} from "@penn/web-common";
import { authStorage } from "@penn/web-common";
import { broadcastLogout } from "@penn/web-common";
import type { RegionCode } from "@penn/web-common";
import { SignOutConfirm } from "../popup/auth/SignOutConfirm";
import { CustomizeAssumptions } from "../popup/profile/CustomizeAssumptions";
import { WorkForStatus } from "../work-for/WorkForStatus";
import { useRefreshProfile } from "@penn/web-common";
import { useTaskNotificationBadge } from "@/hooks/useTaskNotificationBadge";
import { useWorkFor } from "@/hooks/useWorkFor";
import { assumptionsDtoToForm } from "@/utils/assumptions-mapper";
import { workForDisplay } from "@penn/web-common";
import { dispatchCompareCostInvalidate } from "@penn/web-common";
import { SupportModal } from "../popup/SupportModal";
import { AccountView } from "../popup/profile/AccountView";
import { ExistingApplicationsModal } from "../popup/applications/ExistingApplicationsModal";
import { ApplicationEditorModal } from "../popup/applications/ApplicationEditorModal";
import { TasksModal } from "../popup/tasks/TasksModal";
import { PennDropdown, PennText } from "@/components/ui";

/** Web-admin's own dashboard, or null when not configured. */
function adminHomeUrl(base: string | undefined): string | null {
  if (!base) return null;
  try {
    // Operator-supplied, but parsed rather than concatenated: a malformed
    // value must not become a `javascript:` href.
    const url = new URL("/", base);
    if (url.protocol !== "https:" && url.protocol !== "http:") return null;
    return url.toString();
  } catch {
    return null;
  }
}

const navRegionOptions = [
  { value: "EU", label: "Europe" },
  { value: "NA", label: "North America" },
  // Match the region master `displayName` used by registration + Account details
  // (canonical label is "Asia Pacific", not "Asia").
  { value: "ASIA", label: "Asia Pacific" },
];

type Modal =
  | "none"
  | "support"
  | "assumptions"
  | "account"
  | "existing-apps"
  | "new-app"
  | "edit-app"
  | "tasks";

export const NavBar = () => {
  const navigate = useNavigate();
  // Local binding so the click handler keeps the narrowing (imports do not
  // narrow inside closures).
  const registerTarget = registerDestination();
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const [modal, setModal] = useState<Modal>("none");
  const [taskModalView, setTaskModalView] = useState<"tasks" | "mine">("tasks");
  const [jwtToken, setJwtToken] = useState(false);
  const user = useAppSelector((state) => state.auth.user);
  const assumptions = useAppSelector((state) => state.auth.assumptions);
  const selectedRegion = useAppSelector((state) => state.auth.selectedRegion);
  // C3: the apps already applied to the selector workspace (mirrored into Redux
  // by FilterPage) so the "My applications" modal can pre-check them.
  const selectorApps = useAppSelector((state) => state.selector.selectedApps);
  const selectorAppIds = useMemo(
    () => selectorApps.map((a) => a.applicationId),
    [selectorApps],
  );
  const canSwitchRegion = !!user?.role?.canSwitchRegion;
  // Act-As / Work-For is gated purely on `canWorkForOthers`. As of #10
  // (2026-06-28) `distributor_partner` no longer holds this permission (removed
  // via the role_master migration), so distributors no longer see the Work-For
  // selector at all — there is no role-specific hiding here; the missing
  // permission is the single source of truth.
  const canWorkFor = !!user?.role?.canWorkForOthers;
  const canManageUsers = !!user?.role?.canManageUsers;
  const adminUrl = canManageUsers
    ? adminHomeUrl(runtimeEnv("VITE_WEB_ADMIN_URL"))
    : null;
  // POC parity: red dot on the user-menu button while there is pending/new work
  // in the task/quote/approval flow. STAFF-ONLY (admin/sales/engineering watch
  // the region open queue) — external customers/distributors submit RFQs and
  // wait, so they never see it (see useTaskNotificationBadge).
  const { hasNotification } = useTaskNotificationBadge();
  const refreshProfile = useRefreshProfile();
  // Reactive Work-For context (updates live when the selector changes it).
  const workFor = useWorkFor();
  const workForOther = workFor.kind === "other";
  // Stable identity of the current Work-For target (same shape as
  // useAllMyApplications) — used to refetch the effective profile/assumptions
  // whenever the target changes, via ANY path (selector, register-without-otp
  // auto-switch, task-open).
  const workForIdentity =
    workFor.kind === "self" ? "self" : `${workFor.kind}:${workFor.email}`;
  // Issue #1 (SS#1): surface only the "working for" email inside the account
  // chip. Gated on `canWorkForOthers`, matching the old banner's visibility.
  const workForTarget = workForDisplay(workFor);
  const workingForEmail =
    canWorkFor && workForTarget ? workForTarget.email : null;
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);
  const [showAssumptions, setShowAssumptions] = useState(false);
  const [editingAppId, setEditingAppId] = useState<string | null>(null);
  useEffect(() => {
    const jwtToken = authStorage.getItem(TOKEN_STORAGE_KEY);
    setJwtToken(jwtToken ? true : false);
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !user?.id) return;
    // Refetch on sign-in AND on every Work-For target change so the Customize
    // Assumptions modal (and cost math) reflect the target's own assumptions
    // — or global defaults when the target has none.
    void refreshProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refresh per session user + on Work-For target change
  }, [isAuthenticated, user?.id, workForIdentity]);

  // Issue #7: close the account menu on any outside click. The previous
  // full-screen backdrop sat at z-40 while the nav (and its own elements) sit
  // at z-50, so clicks that landed on nav-level content never reached it and
  // the menu stayed open. A document-level mousedown listener scoped to the
  // dropdown container is reliable regardless of stacking context.
  useEffect(() => {
    if (!dropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [dropdownOpen]);

  const handleLogout = async () => {
    // Tear down BOTH the local Penn session and the Amplify-managed Cognito
    // session. Skipping cognitoSignOut() leaves Amplify thinking the user is
    // still signed in, which causes the next /login signIn() to throw
    // "There is already a signed in user".
    //
    // cognitoSignOut() can REJECT (e.g. in a duplicated tab where Amplify's
    // local view of the session differs, or a transient network error). It must
    // never block the local teardown and — critically — the cross-tab
    // broadcast below, otherwise logout sync becomes intermittent ("works
    // sometimes"). Swallow its failure and always finish the local logout.
    // Real server logout first (ADR-0036): it deletes the `auth_sessions` row
    // and clears both cookies. Without it the cookie would survive the local
    // teardown and the next boot refresh would sign the user straight back in.
    // Best-effort like cognitoSignOut() — a failed call must not strand the
    // user in a half-logged-out UI.
    stopSessionRefresh();
    try {
      await logoutRequest();
    } catch {
      /* ignore — proceed with local teardown regardless */
    }
    try {
      await cognitoSignOut();
    } catch {
      /* ignore — proceed with local teardown + broadcast regardless */
    }
    dispatch(setLogout());
    authStorage.removeItem(TOKEN_STORAGE_KEY);
    authStorage.removeItem(USER_STORAGE_KEY);
    // setLogout() clears the persisted Work-For, but the banner's useWorkFor
    // hook only re-reads on this event — fire it so the banner clears now.
    dispatchCompareCostInvalidate();
    // Propagate to every other open tab. This is the DELIBERATE logout, which
    // is the only thing that broadcasts now — an unrecoverable 401 no longer
    // does (ADR-0036).
    broadcastLogout();
    setDropdownOpen(false);
    setShowSignOutConfirm(false);
    if (OIDC_ENABLED) {
      // End the ISSUER session too, not just this app's. The issuer's SSO
      // cookie outlives our storage (Max-Age=86400) and /login silently resumes
      // it, so a local-only sign-out would re-admit the SAME user with no
      // challenge — on a shared browser, to the next person. Full-page
      // navigation: must be last. (broadcastLogout() is synchronous while this
      // only STARTS a navigation, so a sibling tab can still reach /login first;
      // that is why LoginPage never auto-starts on a no-intent arrival.)
      endSession();
      return;
    }
    navigate("/login");
  };

  const initials = user
    ? `${user.firstName?.[0] ?? ""}${user.lastName?.[0] ?? ""}`.toUpperCase() ||
      (user.email[0] ?? "").toUpperCase()
    : "";

  // Staff with `canWorkForOthers` (admin / sales / engineering per the §D.1
  // matrix, plus any custom role granted it) get the shared "Tasks" queue
  // entry. External customers only get "My RFQs / Quotes" (a read-only status
  // view) — the staff "Tasks" queue is hidden for them (GET /v1/tasks is 403
  // for external anyway).
  const isInternalStaff = canWorkFor;
  const menuItems = isInternalStaff
    ? [
        "Account details",
        "Assumptions",
        "My applications",
        "My RFQs / Quotes",
        "Tasks",
      ]
    : ["Account details", "Assumptions", "My applications", "My RFQs / Quotes"];
  // The menu entry that opens the tasks/RFQ queue — staff see a dedicated
  // "Tasks" queue; external customers see it as "My RFQs / Quotes". Used to
  // place the "new task" red dot on the right menu item.
  const tasksMenuLabel = isInternalStaff ? "Tasks" : "My RFQs / Quotes";
  const open = (m: Modal) => setModal(m);
  const close = () => setModal("none");

  const handleMenuAction = (action: string) => {
    switch (action) {
      case "Account details":
        open("account");
        break;
      case "Assumptions":
        open("assumptions");
        break;
      case "My applications":
        open("existing-apps");
        break;
      case "My RFQs / Quotes":
        setTaskModalView("mine");
        open("tasks");
        break;
      case "Tasks":
        setTaskModalView("tasks");
        open("tasks");
        break;
    }
  };

  // Q6 — Configure Solution opens from comparison cards only (FilterPage owns
  // it). NavBar no longer hosts a name-only entry point.

  return (
    <>
      <nav className="w-full bg-linear-to-b from-penn-900 via-penn-700 to-[#005A9E] backdrop-blur-[8px] shadow-soft fixed top-10 z-50">
        <div
          /* 1fr|auto|1fr keeps the center content (title / CTA) exactly
             page-centered — the sides split the leftover space equally and
             only push it off-center when they'd otherwise overlap it. */
          className={`mx-auto px-3 sm:px-6 flex flex-wrap content-center gap-y-1 items-center lg:grid lg:grid-cols-[1fr_minmax(0,auto)_1fr] ${
            pathname === "/installation-system" ? "h-22 lg:h-18" : "h-25"
          }`}
        >
          {/* Logo — left col */}
          <button
            onClick={() => navigate(isAuthenticated || jwtToken ? "/" : "/")}
            className="order-1 lg:order-0 flex items-center gap-2.5 hover:opacity-80 transition-opacity justify-self-start"
          >
            <div
              className={`flex items-center justify-center max-w-[35vw] lg:max-w-none ${
                pathname === "/installation-system" ||
                pathname === "/cad/compare"
                  ? "h-12"
                  : "h-10 lg:h-15 xl:h-20"
              }`}
            >
              <img
                src={logo}
                alt="Installation Systems"
                className="w-full h-full object-contain brightness-0 invert"
              />
            </div>
          </button>

          {/* Center col — centered in the space left by the content-sized side
              columns; own full-width row until the grid kicks in */}
          <div className="order-3 lg:order-0 w-full flex justify-center min-w-0">
            {pathname === "/installation-system" ? (
              <span className="min-w-0 max-w-full lg:px-4">
                <PennText className="block text-h3 sm:text-h2 text-on-navy truncate min-w-0">
                  System Selector &amp; Configurator
                </PennText>
              </span>
            ) : (
              /* ── Landing page: animated CTA button ── */
              <button
                onClick={() => navigate("/installation-system")}
                className="relative px-4 sm:px-7 py-2 sm:py-3 rounded-full bg-penn-500 hover:bg-penn-400 text-white text-button cursor-pointer overflow-hidden group whitespace-nowrap"
                style={{
                  transition:
                    "background-color 0.3s ease, transform 0.15s ease, box-shadow 0.3s ease",
                }}
              >
                {/* Outer slow pulse ring */}
                <span className="absolute -inset-0.75 rounded-full border-2 border-penn-500/60 animate-ping pointer-events-none" />
                {/* Second faster ring */}
                <span
                  className="absolute -inset-1.5 rounded-full border border-penn-400/30 pointer-events-none"
                  style={{
                    animation:
                      "ping 1.8s cubic-bezier(0,0,0.2,1) infinite 0.6s",
                  }}
                />
                {/* Shimmer sweep */}
                <span
                  className="absolute inset-0 rounded-full pointer-events-none"
                  style={{
                    background:
                      "linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.25) 50%, transparent 60%)",
                    backgroundSize: "200% 100%",
                    animation: "shimmer 2.4s ease-in-out infinite",
                  }}
                />
                {/* Hover ripple fill */}
                <span className="absolute inset-0 rounded-full bg-white/15 scale-0 group-hover:scale-100 transition-transform duration-400 ease-out pointer-events-none" />
                {/* Label + arrow */}
                <span className="relative flex items-center gap-2">
                  Find Your Installation System
                  {/* Animated arrow */}
                  <svg
                    className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
                    />
                  </svg>
                </span>
              </button>
            )}
          </div>

          {/* Right col */}
          <div className="order-2 lg:order-0 ml-auto lg:ml-0 flex justify-end">
            {(isAuthenticated || jwtToken) && user ? (
              /* ── Authenticated: user avatar + dropdown ── */
              <div className="flex items-center">
                {canSwitchRegion ? (
                  <div className="mr-1 sm:mr-2 h-9 inline-flex items-center rounded-lg border border-white/30 bg-white/15 shadow-sm focus-within:ring-2 focus-within:ring-penn-400">
                    <span className="hidden sm:inline pl-3 pr-1 text-label text-on-navy-secondary whitespace-nowrap">
                      Region
                    </span>
                    <div className="w-22 sm:w-36 pl-2 sm:pl-0 pr-2">
                      <PennDropdown
                        variant="bare"
                        ariaLabel="Region"
                        options={navRegionOptions}
                        value={selectedRegion ?? user?.region?.code ?? "NA"}
                        onChange={(v) =>
                          dispatch(setSelectedRegion(v as RegionCode))
                        }
                      />
                    </div>
                  </div>
                ) : (
                  /* Users who can't switch region still see their region
                   (read-only) in the nav bar, matching Account details. */
                  user?.region?.displayName && (
                    <div className="mr-1 sm:mr-2 h-9 inline-flex items-center rounded-lg border border-white/30 bg-white/15 shadow-sm">
                      <span className="hidden sm:inline pl-3 pr-1 text-label text-on-navy-secondary whitespace-nowrap">
                        Region
                      </span>
                      <span className="pl-2 sm:pl-0 pr-2 sm:pr-3 text-body-medium text-on-navy whitespace-nowrap">
                        {user.region.displayName}
                      </span>
                    </div>
                  )
                )}
                {workingForEmail && (
                  // Email, not name: it is the one detail every Work-For target has.
                  <div
                    title={`Working for ${workingForEmail}`}
                    data-testid="working-for-chip"
                    className="flex min-w-0 shrink max-w-48 sm:max-w-72 2xl:max-w-96 items-center gap-1.5 rounded-full border border-amber-300/60 bg-amber-300/10 px-3 py-1 text-xs"
                  >
                    <UserCheck
                      aria-hidden="true"
                      className="h-3.5 w-3.5 shrink-0 text-amber-300 2xl:hidden"
                    />
                    <span className="hidden 2xl:inline shrink-0 font-semibold uppercase tracking-wide text-amber-300">
                      Working for
                    </span>
                    <span className="sr-only 2xl:hidden">Working for</span>
                    <span className="truncate font-semibold text-white">
                      {workingForEmail}
                    </span>
                  </div>
                )}
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setDropdownOpen((v) => !v)}
                    // Glass hover, not bg-gray-50: this button sits on the navy
                    // nav, and a near-white hover made the white on-navy label
                    // text disappear.
                    className="relative flex items-center gap-2.5 px-2 sm:px-3 py-1.5 rounded-xl hover:bg-white/15 transition-colors cursor-pointer"
                  >
                    {/* POC-parity "new task" red dot. */}
                    {hasNotification && (
                      <>
                        <span
                          aria-hidden="true"
                          className="absolute top-0.5 right-0.5 h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-white shadow-sm"
                        />
                        <span className="sr-only">
                          You have new task activity
                        </span>
                      </>
                    )}
                    {/* Avatar circle */}
                    <div className="w-8 h-8 rounded-full bg-penn-500 flex items-center justify-center text-white text-small-medium select-none">
                      {initials}
                    </div>
                    <div className="text-left hidden xl:block min-w-0 max-w-36 2xl:max-w-55">
                      <p className="flex items-center gap-2 text-body-lg text-on-navy">
                        <span className="truncate">
                          {user.firstName} {user.lastName}
                        </span>
                      </p>
                    </div>
                    {/* Chevron */}
                    <svg
                      className={`w-4 h-4 text-on-navy-secondary transition-transform ${
                        dropdownOpen ? "rotate-180" : ""
                      }`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  {/* Dropdown — closed on outside click via the mousedown
                    listener above (no backdrop needed). */}
                  {dropdownOpen && (
                    <div className="absolute right-0 mt-2 w-72 max-w-[min(20rem,calc(100vw-2rem))] bg-white rounded-2xl shadow-soft border border-gray-100 z-50 overflow-hidden">
                      {/* User info header */}
                      <div className="px-4 py-3 border-b border-gray-100">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-penn-900 truncate">
                            {user.firstName} {user.lastName}
                          </p>
                          {canManageUsers && (
                            <span className="shrink-0 rounded bg-penn-100 px-1.5 py-0.5 text-[10px] font-semibold tracking-wider text-penn-700">
                              ADMIN
                            </span>
                          )}
                        </div>

                        <p className="text-xs text-gray-400 truncate">
                          {user.email}
                        </p>
                      </div>

                      {canWorkFor && (
                        <WorkForStatus onApplied={() => void refreshProfile()} />
                      )}

                      {adminUrl && (
                        <div className="border-b border-gray-100 py-1">
                          <a
                            href={adminUrl}
                            // Own site, so no noopener: it must be able to
                            // reuse the admin tab.
                            target={ADMIN_TAB_NAME}
                            className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-penn-900 hover:bg-gray-50 transition-colors cursor-pointer"
                          >
                            Admin console
                          </a>
                        </div>
                      )}

                      {/* Menu items */}
                      <div className="py-1">
                        {menuItems.map((item) => {
                          // POC-parity red dot also on the tasks entry: "Tasks"
                          // for staff, "My RFQs / Quotes" for external customers
                          // (their own view of the same queue). Only the primary
                          // tasks item is dotted so staff don't see it twice.
                          const showItemDot =
                            hasNotification && item === tasksMenuLabel;
                          return (
                            <button
                              key={item}
                              onClick={() => {
                                setDropdownOpen(false);
                                handleMenuAction?.(item);
                              }}
                              className="w-full flex items-center justify-between gap-2.5 px-4 py-2.5 text-sm text-penn-900 hover:bg-gray-50 transition-colors cursor-pointer"
                            >
                              <span>{item}</span>
                              {showItemDot && (
                                <>
                                  <span
                                    aria-hidden="true"
                                    className="h-2 w-2 rounded-full bg-red-500 ring-2 ring-white shadow-sm"
                                  />
                                  <span className="sr-only">
                                    new task activity
                                  </span>
                                </>
                              )}
                            </button>
                          );
                        })}
                      </div>
                      <div className="border-t border-gray-100 py-1">
                        <button
                          onClick={() => {
                            setDropdownOpen(false);
                            setShowSignOutConfirm(true);
                          }}
                          className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                        >
                          {/* <svg
                          className="w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                          />
                        </svg> */}
                          Sign out
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              /* ── Guest: Sign in + Register ── */
              <div className="flex items-center gap-2 sm:gap-3 shrink-0">
                <button
                  onClick={() => navigate("/login", { state: { autoStart: true } })}
                  className={`px-3 sm:px-5 py-2 rounded-xl text-xs sm:text-sm border border-gray-200 font-semibold transition-colors cursor-pointer whitespace-nowrap
                ${
                  pathname === "/login"
                    ? "bg-white/20 text-white"
                    : "text-white hover:bg-white/15"
                }`}
                >
                  Sign in
                </button>
                {registerTarget && (
                  <button
                    onClick={() => {
                      // oidc: the platform sign-up door is a different origin,
                      // so this is a full navigation, not a route change.
                      if (registerTarget.kind === "external") {
                        window.location.assign(registerTarget.url);
                        return;
                      }
                      navigate(registerTarget.path);
                    }}
                    className={`px-3 sm:px-5 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap
                ${
                  pathname === "/register"
                    ? "bg-penn-400 text-white"
                    : "bg-penn-500 hover:bg-penn-400 text-white shadow-soft"
                }`}
                  >
                    Register
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </nav>

      {showSignOutConfirm && (
        <SignOutConfirm
          onConfirm={handleLogout}
          onCancel={() => setShowSignOutConfirm(false)}
        />
      )}
      {showAssumptions && (
        <CustomizeAssumptions
          onClose={() => setShowAssumptions(false)}
          onSaved={() => void refreshProfile()}
          initialValues={assumptionsDtoToForm(assumptions)}
          readOnly={workForOther}
        />
      )}
      {/* Modals */}
      <SupportModal open={modal === "support"} onClose={close} />

      {modal === "assumptions" && (
        <CustomizeAssumptions
          onClose={close}
          onSaved={() => {
            void refreshProfile();
            close();
          }}
          initialValues={assumptionsDtoToForm(assumptions)}
          readOnly={workForOther}
        />
      )}

      <AccountView open={modal === "account"} onClose={close} />

      <ExistingApplicationsModal
        open={modal === "existing-apps"}
        onClose={close}
        initialSelectedIds={selectorAppIds}
        onSelect={(apps) => {
          // C3 (POC parity): the NavBar renders outside the selector page, so
          // hand the picked applications to FilterPage via navigation state. It
          // applies them on arrival whether or not the page is already mounted.
          close();
          navigate("/installation-system", { state: { applyApps: apps } });
        }}
        onNew={() => {
          close();
          open("new-app");
        }}
        onEdit={(app) => {
          setEditingAppId(app.applicationId);
          close();
          open("edit-app");
        }}
      />

      <ApplicationEditorModal
        open={modal === "new-app"}
        onClose={close}
        mode="create"
      />
      <ApplicationEditorModal
        open={modal === "edit-app"}
        onClose={() => {
          setEditingAppId(null);
          close();
        }}
        mode="edit"
        applicationId={editingAppId}
      />
      <TasksModal
        open={modal === "tasks"}
        onClose={close}
        view={taskModalView}
      />
    </>
  );
};
